import { Component } from "react"

/* Components */
import { Input } from "../../components"

/* Modal */
import Modal from "react-modal"

/* Redux */
import { connect } from "react-redux"

/* Translate */
import { t } from "../../translates/translate"

/* REST API */
import { variables, get, create, edit } from "../../api/Dealer"

/* Helpers */
import { utils } from "../../helpers"

/* Icons */
import { CloseIcon, CheckIcon, InfoIcon, StudioIcon, TableIcon } from "./DealerIcons"

/* Sweet alert */
import Swal from "../../helpers/alert"


/* EMPTY FORM STATE */
const EMPTY = {
    id: 0,
    type: "create",
    status: "loading",
    studios: [],
    tables: [],
    name: "",
    pincode: "",
    studioID: 0,
    tableIDs: [],
    waiting: false,
}

/* PIN LENGTH — THE DEALER MONITOR SCANS A SIX CHARACTER BARCODE */
const PIN_LENGTH = 6


/* Widget Dealer Form */
class DealerForm extends Component {

    constructor() {
        super()
        this.state = { visible: false, ...EMPTY }
    }

    /* Open Action */
    open = ({ type = "create", id = 0 }) => {

        this.setState({ visible: true, ...EMPTY, type, id: parseInt(id) ? parseInt(id) : 0 }, () => {
            this.load()
        })
    }

    /* Close Action */
    close = () => {
        this.setState({ visible: false, ...EMPTY })
    }

    /* Load Action — create reads the variables, edit reads the dealer together with them */
    load = () => {

        /* Fields */
        const { type, id } = this.state

        /* Set Status */
        this.setState({ status: "loading" })

        /* Edit */
        if (type === "edit") {

            get({ id }).then((response) => {
                if (response.status === 200) {
                    this.setState({
                        status: "ok",
                        studios: response.data.studios,
                        tables: response.data.tables,
                        name: utils.checkValue(response.data.dealer.name, ""),
                        pincode: utils.checkValue(response.data.dealer.pincode, ""),
                        studioID: parseInt(response.data.dealer.studioID) ? parseInt(response.data.dealer.studioID) : 0,
                        tableIDs: response.data.tableIDs,
                    })
                }
                else {
                    this.setState({ status: "error" })
                    Swal.fire(utils.notification(this.message(response), "error"))
                    this.close()
                }
            }).catch(() => {
                this.setState({ status: "error" })
                Swal.fire(utils.notification(t("swal.generic"), "error"))
                this.close()
            })

            return
        }

        /* Create */
        variables().then((response) => {
            if (response.status === 200) {
                this.setState({ status: "ok", studios: response.data.studios, tables: response.data.tables })
            }
            else {
                this.setState({ status: "error" })
                Swal.fire(utils.notification(this.message(response), "error"))
                this.close()
            }
        }).catch(() => {
            this.setState({ status: "error" })
            Swal.fire(utils.notification(t("swal.generic"), "error"))
            this.close()
        })

    }

    /* Read The Error Message Of A Response */
    message = (response) => {

        if (response && response.data && response.data.message) {
            return response.data.message
        }

        if (response && response.status === 498) {
            return t("swal.invalidKey")
        }

        if (response && (response.status === 401 || response.status === 403)) {
            return t("swal.noAccess")
        }

        return t("swal.generic")
    }

    /* Generate Pincode */
    generate = () => {

        /* Fields */
        let pincode = ""

        /* Collect Digits */
        for (let index = 0; index < PIN_LENGTH; index++) {
            pincode += Math.floor(Math.random() * 10)
        }

        this.setState({ pincode })
    }

    /* Tables Of The Selected Studio */
    getTables = () => {

        /* Fields */
        const { tables, studioID } = this.state

        /* Send Result */
        return tables.filter((item) => parseInt(item.studioID) === parseInt(studioID))
    }

    /* Change Studio — the table access of the previous studio is dropped */
    changeStudio = (id) => {

        /* Fields */
        const { studioID, type } = this.state
        const value = parseInt(id)

        /* A Dealer Never Moves Between Studios — PitBoard keeps the history of each studio apart */
        if (type === "edit") {
            return
        }

        /* Check Selection */
        if (parseInt(studioID) === value) {
            return
        }

        this.setState({ studioID: value, tableIDs: [] })
    }

    /* Toggle Table */
    toggleTable = (id) => {

        /* Fields */
        const { tableIDs } = this.state
        const tableID = parseInt(id)

        /* Check Selection */
        if (tableIDs.includes(tableID)) {
            this.setState({ tableIDs: tableIDs.filter((item) => parseInt(item) !== tableID) })
        }
        else {
            this.setState({ tableIDs: [...tableIDs, tableID] })
        }
    }

    /* Toggle All Tables */
    toggleAllTables = () => {

        /* Fields */
        const { tableIDs } = this.state
        const tables = this.getTables()

        /* Check Selection */
        if (tableIDs.length === tables.length) {
            this.setState({ tableIDs: [] })
        }
        else {
            this.setState({ tableIDs: tables.map((item) => parseInt(item.id)) })
        }
    }

    /* Save Action */
    save = () => {

        /* Fields */
        const { type, id, name, pincode, studioID, tableIDs } = this.state
        const { reload } = this.props

        /* Check Name */
        if (!name) {
            this._name.error(t("dealer.error.emptyName"))
            return
        }

        /* Check Pincode */
        if (!pincode) {
            this._pincode.error(t("dealer.error.emptyPin"))
            return
        }

        /* Check Pincode Length */
        if (pincode.length !== PIN_LENGTH) {
            this._pincode.error(t("dealer.error.pinLength"))
            return
        }

        /* Check Studio */
        if (!studioID) {
            Swal.fire(utils.notification(t("dealer.error.selectStudio"), "error"))
            return
        }

        /* Start Waiting */
        this.setState({ waiting: true })

        /* Prepare Request */
        const request = type === "edit"
            ? edit({ id, name, pincode, studioID, tableIDs })
            : create({ name, pincode, studioID, tableIDs })

        /* Send to REST API */
        request.then((response) => {
            if (response.status === 200) {
                Swal.fire(utils.notification(type === "edit" ? t("dealer.savedSuccess") : t("dealer.addedSuccess"), "success"))
                reload()
                this.close()
            }
            else {
                Swal.fire(utils.notification(this.message(response), "error"))
            }
        }).catch(() => {
            Swal.fire(utils.notification(t("swal.generic"), "error"))
        }).finally(() => {
            this.setState({ waiting: false })
        })

    }

    /* Draw Studios — a dealer belongs to exactly one studio */
    _studios = () => {

        /* Fields */
        const { studioID, type } = this.state

        /* An Edit Shows The Own Studio Only — it can not be changed */
        const studios = type === "edit" ? this.state.studios.filter((item) => parseInt(item.id) === parseInt(studioID)) : this.state.studios

        return (
            <div className="dealer--section">

                <div className="dealer--section-head">
                    <div className="dealer--section-title">
                        <StudioIcon />
                        {t("dealer.studio")}
                    </div>
                    {type === "edit" ? <div className="dealer--chips-empty">{t("dealer.studioLocked")}</div> : null}
                </div>

                {studios.length === 0
                    ? <div className="dealer--chips-empty">{t("dealer.noStudios")}</div>
                    : <div className="dealer--chips">
                        {studios.map((item, index) =>
                            <div
                                onClick={() => this.changeStudio(item.id)}
                                className={`dealer--chip round green ${parseInt(studioID) === parseInt(item.id) ? "active" : ""}`}
                                key={`${index}`}
                            >
                                <div className="dealer--chip-mark"><CheckIcon size={11} /></div>
                                <div className="dealer--chip-text">
                                    <div className="dealer--chip-name">{item.name}</div>
                                    {item.slug ? <div className="dealer--chip-slug">{item.slug}</div> : null}
                                </div>
                            </div>
                        )}
                    </div>
                }

            </div>
        )
    }

    /* Draw Table Access — only the tables of the selected studio */
    _tables = () => {

        /* Fields */
        const { studioID, tableIDs } = this.state
        const tables = this.getTables()

        /* Studio Is Not Selected Yet */
        if (!studioID) {
            return (
                <div className="dealer--section">
                    <div className="dealer--hint">
                        <InfoIcon />
                        <span>{t("dealer.selectStudioHint")}</span>
                    </div>
                </div>
            )
        }

        return (
            <div className="dealer--section">

                <div className="dealer--section-head">
                    <div className="dealer--section-title">
                        <TableIcon />
                        {t("dealer.tableAccess")}
                        <span className={`dealer--counter ${tableIDs.length === 0 ? "empty" : ""}`}>{tableIDs.length} / {tables.length}</span>
                    </div>

                    {tables.length > 0 &&
                        <div onClick={() => this.toggleAllTables()} className="dealer--link">
                            {tableIDs.length === tables.length ? t("common.clearAll") : t("common.selectAll")}
                        </div>
                    }
                </div>

                {tables.length === 0
                    ? <div className="dealer--chips-empty">{t("dealer.noTables")}</div>
                    : <div className="dealer--chips">
                        {tables.map((item, index) =>
                            <div
                                onClick={() => this.toggleTable(item.id)}
                                className={`dealer--chip ${tableIDs.includes(parseInt(item.id)) ? "active" : ""}`}
                                key={`${index}`}
                            >
                                <div className="dealer--chip-mark"><CheckIcon size={11} /></div>
                                <div className="dealer--chip-text">
                                    <div className="dealer--chip-name">{item.name}</div>
                                    {item.slug ? <div className="dealer--chip-slug">{item.slug}</div> : null}
                                </div>
                            </div>
                        )}
                    </div>
                }

                <div className="dealer--section">
                    <div className="dealer--hint">
                        <InfoIcon />
                        <span>{t("dealer.tableAccessHint")}</span>
                    </div>
                </div>

            </div>
        )
    }

    /* Draw Body */
    _body = () => {

        /* Fields */
        const { status, name, pincode } = this.state

        /* Check Status */
        if (status !== "ok") {
            return <div className="dealer--modal-loading"><div className="dealer--loading" /></div>
        }

        return (
            <div className="dealer--modal-body">

                <Input ref={(ref) => this._name = ref} label={t("dealer.dealerName")} value={name} onChange={(event) => this.setState({ name: event.target.value })} />
                <Input ref={(ref) => this._pincode = ref} label="PIN / Barcode" value={pincode} onChange={(event) => this.setState({ pincode: event.target.value })} />

                <div onClick={() => this.generate()} className="dealer--generate">{t("dealer.generatePin")}</div>

                {/* Draw Studios */}
                {this._studios()}

                {/* Draw Table Access */}
                {this._tables()}

            </div>
        )
    }

    /* Draw Footer */
    _footer = () => {

        /* Fields */
        const { status, waiting } = this.state

        /* Check Status */
        if (status !== "ok") {
            return null
        }

        return (
            <div className="dealer--modal-footer">

                <div onClick={() => this.close()} className="dealer--button ghost">{t("common.cancel")}</div>

                {waiting
                    ? <div className="dealer--button primary"><div className="dealer--loading small" /></div>
                    : <div onClick={() => this.save()} className="dealer--button primary">{t("common.save")}</div>
                }

            </div>
        )
    }

    render = () => {

        /* Fields */
        const { visible, type } = this.state

        return (
            <Modal
                isOpen={visible}
                onRequestClose={() => this.close()}
                className="dealer--modal"
                overlayClassName="dealer--overlay"
                closeTimeoutMS={200}
            >

                <div className="dealer--modal-header">

                    <div>
                        <h2>{type === "edit" ? t("dealer.editTitle") : t("dealer.addTitle")}</h2>
                        <p>{type === "edit" ? t("dealer.editSubtitle") : t("dealer.createSubtitle")}</p>
                    </div>

                    <div onClick={() => this.close()} className="dealer--modal-close">
                        <CloseIcon />
                    </div>

                </div>

                {/* Draw Body */}
                {this._body()}

                {/* Draw Footer */}
                {this._footer()}

            </Modal>
        )
    }

}

const mapStateToProps = (state) => ({
    language: state.language,
})

export default connect(mapStateToProps, null, null, { forwardRef: true })(DealerForm)
